import type { RayHit, Player } from './types';

export class Raycaster {
  private worldMap: number[];
  private mapWidth: number;
  private mapHeight: number;

  constructor() {
    this.worldMap = [];
    this.mapWidth = 0;
    this.mapHeight = 0;
  }

  setMap(tiles: number[], width: number, height: number): void {
    this.worldMap = tiles;
    this.mapWidth = width;
    this.mapHeight = height;
  }

  // Vérifie si une tuile est solide (mur fermé, pas une porte ouverte)
  private tileAt(x: number, y: number): number {
    const tx = Math.floor(x);
    const ty = Math.floor(y);
    if (tx < 0 || ty < 0 || tx >= this.mapWidth || ty >= this.mapHeight) {
      return 1; // Hors limites = mur
    }
    return this.worldMap[ty * this.mapWidth + tx] || 0;
  }

  // Set de tiles qui bloquent le mouvement
  private blockingTiles = new Set([1, 3]); // 1 = mur, 3 = porte fermée

  canMove(x: number, y: number): boolean {
    const tile = this.tileAt(x, y);
    return !this.blockingTiles.has(tile);
  }

  // Lance un rayon DDA amélioré (compatible avec votre code existant)
  castRay(player: Player, rayDirX: number, rayDirY: number): RayHit {
    const posX = player.x;
    const posY = player.y;

    // Position sur la grille
    let mapX = Math.floor(posX);
    let mapY = Math.floor(posY);

    // Longueur du rayon d'un côté X ou Y au suivant
    const deltaDistX = Math.abs(1 / rayDirX);
    const deltaDistY = Math.abs(1 / rayDirY);

    let perpWallDist: number;
    let stepX: number;
    let stepY: number;
    let sideDistX: number;
    let sideDistY: number;

    // Direction de step et distance initiale
    if (rayDirX < 0) {
      stepX = -1;
      sideDistX = (posX - mapX) * deltaDistX;
    } else {
      stepX = 1;
      sideDistX = (mapX + 1.0 - posX) * deltaDistX;
    }

    if (rayDirY < 0) {
      stepY = -1;
      sideDistY = (posY - mapY) * deltaDistY;
    } else {
      stepY = 1;
      sideDistY = (mapY + 1.0 - posY) * deltaDistY;
    }

    // DDA
    let hit = false;
    let side = 0; // 0 = vertical, 1 = horizontal
    let wallType = 0;

    while (!hit) {
      // Avancer dans la grille
      if (sideDistX < sideDistY) {
        sideDistX += deltaDistX;
        mapX += stepX;
        side = 0;
      } else {
        sideDistY += deltaDistY;
        mapY += stepY;
        side = 1;
      }

      // Vérifier collision
      wallType = this.tileAt(mapX, mapY);
      if (wallType > 0) {
        hit = true;
      }
    }

    // Calculer distance perpendiculaire (évite fish-eye)
    if (side === 0) {
      perpWallDist = (mapX - posX + (1 - stepX) / 2) / rayDirX;
    } else {
      perpWallDist = (mapY - posY + (1 - stepY) / 2) / rayDirY;
    }

    // Position exacte sur le mur (pour mapping texture)
    let wallX: number;
    if (side === 0) {
      wallX = posY + perpWallDist * rayDirY;
    } else {
      wallX = posX + perpWallDist * rayDirX;
    }
    wallX -= Math.floor(wallX);

    return {
      distance: perpWallDist,
      perpWallDist,
      wallType,
      side,
      hitX: mapX,
      hitY: mapY,
      wallX
    };
  }

  // Vérifie collision avec un rayon de collision
  checkCollisionRadius(x: number, y: number, radius: number = 0.25): boolean {
    // Vérifier 8 points autour du joueur
    const points = [
      { x: x - radius, y: y - radius },
      { x: x, y: y - radius },
      { x: x + radius, y: y - radius },
      { x: x - radius, y: y },
      { x: x + radius, y: y },
      { x: x - radius, y: y + radius },
      { x: x, y: y + radius },
      { x: x + radius, y: y + radius }
    ];

    for (const p of points) {
      const tile = this.tileAt(p.x, p.y);
      if (this.blockingTiles.has(tile)) {
        return true;
      }
    }

    return false;
  }

  // Pousse le joueur hors des murs (smooth collision)
  pushOutOfWall(player: Player, pushDist: number = 0.25): void {
    const x = player.x;
    const y = player.y;
    const tx = Math.floor(x);
    const ty = Math.floor(y);

    // Vérifier les 4 directions cardinales
    const checks = [
      { dx: -1, dy: 0, fx: tx, fy: ty },
      { dx: 1, dy: 0, fx: tx + 1, fy: ty },
      { dx: 0, dy: -1, fx: tx, fy: ty },
      { dx: 0, dy: 1, fx: tx, fy: ty + 1 }
    ];

    for (const check of checks) {
      const tile = this.tileAt(tx + check.dx, ty + check.dy);
      if (this.blockingTiles.has(tile)) {
        if (check.dx !== 0) {
          const dist = Math.abs(x - check.fx);
          if (dist < pushDist) {
            player.x += check.dx > 0 ? -(pushDist - dist) : (pushDist - dist);
          }
        }
        if (check.dy !== 0) {
          const dist = Math.abs(y - check.fy);
          if (dist < pushDist) {
            player.y += check.dy > 0 ? -(pushDist - dist) : (pushDist - dist);
          }
        }
      }
    }
  }

  // Ouvrir une porte
  openDoor(x: number, y: number): boolean {
    if (x < 0 || y < 0 || x >= this.mapWidth || y >= this.mapHeight) {
      return false;
    }
    const idx = y * this.mapWidth + x;
    if (this.worldMap[idx] === 3) { // 3 = porte fermée
      this.worldMap[idx] = 0; // Ouvrir
      return true;
    }
    return false;
  }
}
